import { inject, Injectable, Signal } from '@angular/core';
import { UserClient, UserDto } from '@lib-ui/client';
import { merge, of, Subject } from 'rxjs';
import { localStorageKeys } from '@lib-ui/lib/models/local-storage-keys';
import { catchError, map, shareReplay, switchMap, tap } from 'rxjs/operators';
import { toSignal } from '@angular/core/rxjs-interop';
import { LoggingService } from '@lib-ui/lib/services/logging.service';
import { LoadingState, ServiceState } from '@lib-ui/lib/models';

interface UserState extends ServiceState {
  user: Signal<UserDto | null>;
}

@Injectable({
  providedIn: 'root',
})
export class UserService {
  private readonly userClient = inject(UserClient);
  private readonly loggingService = inject(LoggingService);
  private readonly load$ = new Subject<string | null>();
  private readonly error$ = new Subject<Error | null>();
  private readonly user$ = this.load$.pipe(
    switchMap(externalUserId => {
      if (!externalUserId) {
        return of(null);
      }
      return this.userClient.getUserByExternalId(externalUserId).pipe(
        catchError((error) => {
          this.error$.next(error);
          return of(null);
        }),
      );
    }),
    tap(user => this.storeUserInLocalStorage(user)),
    shareReplay(1),
  );

  private readonly status$ = merge(
    this.user$.pipe(map(() => LoadingState.Success)),
    this.error$.pipe(map(() => LoadingState.Error)),
    this.load$.pipe(map(() => LoadingState.Pending)),
  );

  private readonly user = toSignal(this.user$, { initialValue: null });
  private readonly status = toSignal(this.status$, { initialValue: LoadingState.Pending });
  private readonly error = toSignal(this.error$, { initialValue: null });

  public state: UserState = {
    user: this.user,
    status: this.status,
    error: this.error,
  };

  public getUserByExternalId(externalUserId: string) {
    this.load$.next(externalUserId);
  }

  public getInspectors(user: UserDto | null) {
    if (!user) {
      return of([]);
    }
    return this.userClient.getInspectors().pipe(
      map(inspectors => {
        const sortedInspectors = this.sortInspectors(inspectors, user);
        return sortedInspectors.reduce((prev: UserDto[], current: UserDto) => {
          return [...prev, current];
        }, []);
      }),
    );
  }


  private sortInspectors(
    inspectors: UserDto[],
    user: UserDto | null,
  ): UserDto[] {
    if (!user || inspectors.length <= 0) {
      return [];
    }
    if (!user.teamName || user.teamName === '') {
      return inspectors;
    }

    const otherTeams = inspectors.filter(inspector => inspector.teamName !== user.teamName);
    const foundTeam = inspectors.filter(inspector => inspector.teamName === user.teamName);
    return [...foundTeam, ...otherTeams];
  }

  logout() {
    this.load$.next(null);
  }


  excelExportInspectors() {
    return this.userClient.excelExportInspectors().pipe(
      catchError((error) => {
        this.loggingService.logError(error);
        return of([]);
      }),
    );
  }

  // No Service for local storage
  // eslint-disable-next-line class-methods-use-this
  private storeUserInLocalStorage(user: UserDto | null) {
    if (!user) {
      localStorage.removeItem(localStorageKeys.USER_KEY);
      return;
    }
    localStorage.setItem(localStorageKeys.USER_KEY, JSON.stringify(user));
  }
}
